import { getTimeZones, type TimeZone } from "@vvo/tzdb";
import { useMemo } from "react";

const COMMON_TIME_ZONES = new Set([
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "Europe/London",
  "Europe/Paris",
  "Asia/Tokyo",
  "Asia/Shanghai",
  "Asia/Kolkata",
  "Australia/Sydney",
  "UTC",
]);

/**
 * A hook that returns a list of common time zones, including the selected time zone if it is not in the common time zones.
 */
export function useCommonTimeZones(selectedTimeZoneName: string): TimeZone[] {
  const allTimeZones = useMemo(() => getTimeZones(), []);

  const commonTimeZones = useMemo(
    () => allTimeZones.filter((tz) => COMMON_TIME_ZONES.has(tz.name)),
    [allTimeZones]
  );

  const commonTimeZonesWithSelected = useMemo(() => {
    if (COMMON_TIME_ZONES.has(selectedTimeZoneName)) {
      return commonTimeZones;
    }
    const unusualTimeZone = allTimeZones.find((tz) => tz.name === selectedTimeZoneName);
    if (!unusualTimeZone) {
      console.error(`Time zone ${selectedTimeZoneName} not found in all time zones.`);
      return commonTimeZones;
    }
    return [unusualTimeZone, ...commonTimeZones];
  }, [selectedTimeZoneName, allTimeZones, commonTimeZones]);

  return commonTimeZonesWithSelected;
}
